import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import {
  Archive,
  DollarSign,
  Package,
  Search,
  ChevronDown,
  Check,
  Filter,
} from 'lucide-react';
import { ProductsService } from '../../services/products/products.service';

const formatMoney = (amount: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const KPICard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}> = ({ title, value, subtitle, icon, iconBg, iconColor }) => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-xl ${iconBg}`}>
        <div className={iconColor}>{icon}</div>
      </div>
    </div>
  </div>
);

const KPICardSkeleton = () => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
        <div className="h-8 bg-gray-200 rounded w-32" />
      </div>
      <div className="p-3 rounded-xl bg-gray-200 w-12 h-12" />
    </div>
  </div>
);

const MOTIVOS = [
  { value: '', label: 'Todos los motivos' },
  { value: 'VENCIDO', label: 'Vencido' },
  { value: 'DAÑADO', label: 'Dañado' },
  { value: 'ROBO', label: 'Robo' },
  { value: 'PERDIDA', label: 'Pérdida' },
  { value: 'DEVOLUCION', label: 'Devolución' },
  { value: 'AJUSTE_INVENTARIO', label: 'Ajuste de inventario' },
  { value: 'OTRO', label: 'Otro' },
];

export const ProductosBajaContent: React.FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [motivo, setMotivo] = useState<string>('');
  const limit = 20;

  const query = useQuery({
    queryKey: ['productos-bajas', page, search, motivo],
    queryFn: () => ProductsService.listarBajas(page, limit, search || undefined, motivo || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const result = query.data;
  const bajas = result?.success ? result.data : [];
  const meta = result?.meta;

  const kpis = useMemo(() => {
    const totalBajas = meta?.total || 0;
    const cantidadTotal = bajas.reduce((sum: number, b: any) => sum + (b.cantidad || 0), 0);
    // Valor estimado: usamos el precio_compra del producto si está disponible
    const valorEstimado = bajas.reduce((sum: number, b: any) => {
      const precio = b.producto?.precioCompra || b.producto?.precio || 0;
      return sum + (precio * (b.cantidad || 0));
    }, 0);
    return { totalBajas, cantidadTotal, valorEstimado };
  }, [bajas, meta]);

  const isLoading = query.isLoading && !query.data;

  return (
    <div>
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar producto..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-9 pr-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-64"
          />
        </div>

        <Listbox value={motivo} onChange={(v) => { setMotivo(v); setPage(1); }}>
          <div className="relative">
            <ListboxButton className="relative w-[200px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
              <span className="block truncate">
                {MOTIVOS.find((m) => m.value === motivo)?.label}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              {MOTIVOS.map((opt) => (
                <ListboxOption
                  key={opt.value}
                  value={opt.value}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                >
                  <span className="block truncate">{opt.label}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                    <Check className="h-4 w-4" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        <button
          onClick={() => query.refetch()}
          className="p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          title="Recargar datos"
        >
          <Filter className="w-5 h-5" />
        </button>
      </div>

      {/* KPIs */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <KPICard
            title="Total Bajas Registradas"
            value={kpis.totalBajas.toLocaleString('es-CO')}
            subtitle="Registros históricos"
            icon={<Archive className="w-6 h-6" />}
            iconBg="bg-gray-100"
            iconColor="text-gray-600"
          />
          <KPICard
            title="Unidades Dadas de Baja"
            value={kpis.cantidadTotal.toLocaleString('es-CO')}
            subtitle="Cantidad total"
            icon={<Package className="w-6 h-6" />}
            iconBg="bg-red-100"
            iconColor="text-red-600"
          />
          <KPICard
            title="Valor Estimado de Pérdida"
            value={formatMoney(kpis.valorEstimado)}
            subtitle="Según precio de compra"
            icon={<DollarSign className="w-6 h-6" />}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
          />
        </div>
      )}

      {/* Tabla */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Historial de Bajas de Productos</h3>
          <span className="text-sm text-gray-500">
            Página {page} de {meta?.totalPages || 1}
          </span>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : bajas.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            No hay bajas registradas
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Observaciones</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bajas.map((b: any) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium text-gray-900">{b.producto?.nombre || '—'}</p>
                      <p className="text-xs text-gray-500">{b.producto?.categoria || '—'}</p>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        b.motivo === 'VENCIDO' ? 'bg-red-100 text-red-700' :
                        b.motivo === 'DAÑADO' ? 'bg-orange-100 text-orange-700' :
                        b.motivo === 'ROBO' ? 'bg-purple-100 text-purple-700' :
                        b.motivo === 'PERDIDA' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {b.motivo}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-sm font-medium text-gray-900">{b.cantidad}</td>
                    <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate">{b.observaciones || '—'}</td>
                    <td className="px-5 py-3 text-sm text-gray-500">{b.usuario?.nombreCompleto || '—'}</td>
                    <td className="px-5 py-3 text-center text-sm text-gray-500">{formatDate(b.fechaBaja)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {meta && meta.totalPages > 1 && (
          <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-500">
              Página {page} de {meta.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page >= meta.totalPages}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
